import React from 'react';
import { Alert, Card, CardBody, CardHeader, FormGroup, Label, Button, Input } from 'reactstrap';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DataHandler from '../../../services/DataHandler';
import mailIcon from '../../../assets/images/icons/Correo.svg';
import docIcon from '../../../assets/images/icons/CIPasaporte.svg';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, EXTERNAL_URLS } from '../../../services/Endpoints';


/**
 * Primer paso del wizard de Activar Cuenta.
 * onNext(values) -> Promise que devuelve datos del API para el siguiente paso.
 */
const RegistroCuentaPaso1 = ({ onNext }) => {
  const { t, i18n } = useTranslation('activation');
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'es';

  //  Link dinámico para el instructivo según idioma
  const instructivoUrl =
    currentLang === 'en'
      ? EXTERNAL_URLS.INSTRUCTIVO_ACTIVACION_EN
      : EXTERNAL_URLS.INSTRUCTIVO_ACTIVACION_ES;
  const termcondicUrl =
    currentLang === 'en'
      ? EXTERNAL_URLS.PDF_PRIVACIDAD_EN
      : EXTERNAL_URLS.PDF_PRIVACIDAD_ES;
      


  // Validación con Yup (ya usando t() con fallback en español)
  const validationSchema = Yup.object({
    correoelectronico: Yup.string()
      .email(t('step1.errors.emailInvalid', 'Email inválido'))
      .required(t('step1.errors.emailRequired', 'Email obligatorio')),
    confirmarcorreoelectronico: Yup.string()
      .oneOf(
        [Yup.ref('correoelectronico')],
        t('step1.errors.emailsMustMatch', 'Emails deben coincidir')
      )
      .required(
        t(
          'step1.errors.confirmEmailRequired',
          'Confirmar email obligatorio'
        )
      ),
    numerodocumento: Yup.string()
      .matches(/^[0-9]{10}$/, t('step1.errors.doc10Digits', 'El documento debe tener 10 dígitos'))
      .required(t('step1.errors.docRequired', 'Documento obligatorio')),
    confirmarterminosycondiciones: Yup.boolean().oneOf(
      [true],
      t(
        'step1.errors.mustAcceptTerms',
        'Debes aceptar Términos y Condiciones'
      )
    ),
  });

  return (
    <Card className="shadow-sm">
      <CardHeader tag="h4" className="card-header-primary">
        {t('step1.title', 'Paso 1: Información Básica')}
      </CardHeader>
      <CardBody>
        <Formik
          initialValues={{
            correoelectronico: '',
            confirmarcorreoelectronico: '',
            numerodocumento: '',
            confirmarterminosycondiciones: false,
          }}
          validationSchema={validationSchema}
          onSubmit={async (vals, { setSubmitting, setStatus }) => {
            setStatus(null);
            try {
              // Construir payload unificado de checks
              const payload = {
                correoelectronico: vals.correoelectronico,
                confirmarcorreoelectronico: vals.confirmarcorreoelectronico,
                numerodocumento: vals.numerodocumento,
                confirmaredad: vals.confirmarterminosycondiciones,
                confirmarresidencia: vals.confirmarterminosycondiciones,
                confirmarcentralriesgo: vals.confirmarterminosycondiciones,
                lang: currentLang, // Añadido: parámetro de idioma
              };
              const dh = new DataHandler('POSTREGISTRO', null, null, () => { }, () => { }, () => { });
              const result = await dh.postData(payload);
              if (result.succeeded) {
                onNext(result.data);
              } else {
                setStatus(
                  result.message ||
                  t('step1.errors.activateAccountError', 'Error activando cuenta')
                );
              }
            } catch (err) {
              setStatus(
                err.message ||
                t('step1.errors.connectionError', 'Error de conexión')
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <Form>
              {/* Email */}
              <FormGroup className="mb-3">
                <Label for="correoelectronico">
                  {t('step1.emailLabel', 'Email')}
                </Label>
                <div
                  className="d-flex align-items-center border"
                  style={{
                    borderRadius: '45px',
                    padding: '0.5rem 1rem',
                  }}
                >
                  <img
                    src={mailIcon}
                    alt="Email Icon"
                    style={{ width: '20px', marginRight: '10px' }}
                  />
                  <Field
                    name="correoelectronico"
                    as={Input}
                    placeholder={t(
                      'step1.emailPlaceholder',
                      'Correo Electrónico'
                    )}
                    className="border-0"
                  />
                </div>
                <ErrorMessage
                  name="correoelectronico"
                  component="div"
                  className="text-danger small mt-1"
                />
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="confirmarcorreoelectronico">
                  {t('step1.confirmEmailLabel', 'Confirmar Email')}
                </Label>
                <div
                  className="d-flex align-items-center border"
                  style={{
                    borderRadius: '45px',
                    padding: '0.5rem 1rem',
                  }}
                >
                  <img
                    src={mailIcon}
                    alt="Confirm Email Icon"
                    style={{ width: '20px', marginRight: '10px' }}
                  />
                  <Field
                    name="confirmarcorreoelectronico"
                    as={Input}
                    placeholder={t(
                      'step1.confirmEmailPlaceholder',
                      'Confirmar Correo'
                    )}
                    className="border-0"
                  />
                </div>
                <ErrorMessage
                  name="confirmarcorreoelectronico"
                  component="div"
                  className="text-danger small mt-1"
                />
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="numerodocumento">
                  {t('step1.documentLabel', 'Número de Documento')}
                </Label>
                <div
                  className="d-flex align-items-center border"
                  style={{
                    borderRadius: '45px',
                    padding: '0.5rem 1rem',
                  }}
                >
                  <img
                    src={docIcon}
                    alt="Document Icon"
                    style={{ width: '20px', marginRight: '10px' }}
                  />
                  <Field
                    name="numerodocumento"
                    as={Input}
                    placeholder={t('step1.documentPlaceholder', 'Documento')}
                    maxLength="10"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }}
                    className="border-0"
                  />
                </div>
                <ErrorMessage
                  name="numerodocumento"
                  component="div"
                  className="text-danger small mt-1"
                />
              </FormGroup>

              <FormGroup check className="mt-3">
                <Label check className="form-check-label">
                  <Field
                    type="checkbox"
                    name="confirmarterminosycondiciones"
                    className="form-check-input me-2"
                  />
                  {t(
                    'step1.acceptTermsLabel',
                    'Acepto los Términos y Condiciones'
                  )}
                  <a
                    href={termcondicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-1"
                  >
                    {t('common.view', 'Ver')}
                  </a>
                </Label>
                <ErrorMessage
                  name="confirmarterminosycondiciones"
                  component="div"
                  className="text-danger small ms-4 mt-1"
                />
              </FormGroup>

              {/*  Instructivo con link dinámico según idioma */}
              <Label check className="form-check-label mt-3 d-block">
                {t(
                  'step1.instructionsLabel',
                  'Instructivo para Activación de Cuenta'
                )}
                <a
                  href={instructivoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-1"
                >
                  {t('common.view', 'Ver')}
                </a>
              </Label>

              {status && (
                <Alert color="danger" className="mt-4">
                  {status}
                </Alert>
              )}

              <div className="d-flex justify-content-end mt-4">
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {isSubmitting
                    ? t('common.processing', 'Procesando...')
                    : t('common.next', 'Siguiente →')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>
  );
};

export default RegistroCuentaPaso1;
